<template>
  <div class="task-list" id="task-list">
    <div v-if="fileList.length === 0" class="empty-list" id="empty-list">
      <i class="fas fa-inbox"></i>
      <p>{{ $t('manga.fileList.empty') }}</p>
    </div>

    <!-- 全选控制栏 -->
    <div v-if="fileList.length > 0" class="select-all-bar">
      <el-checkbox v-model="selectAll" @change="handleSelectAll" :indeterminate="isIndeterminate">
        {{ $t('manga.fileList.selectAll', { selected: selectedCount, max: Math.min(20, fileList.filter(f => f.status === '已完成').length) }) }}
      </el-checkbox>
    </div>

    <!-- 任务列表 -->
    <div v-for="file in fileList" :key="file.id" class="task-item">
      <div class="task-checkbox">
        <el-checkbox
          v-model="selectedFiles[file.id]"
          @change="handleFileSelect(file.id)"
          :disabled="file.status !== '已完成'"
        />
      </div>
      <div class="task-info">
        <div class="task-name">{{ file.fileName }}</div>
        <div class="task-time" :class="getStatusClass(file.status)">{{ displayStatus(file.status) }}</div>
      </div>
      <div class="task-actions">
        <el-button v-if="file.status === '已完成'" class="action-btn view-btn" @click="handlePreview(file)" size="small"
          type="primary" circle>
          <i class="fas fa-search"></i>
        </el-button>
        <el-button v-if="file.status === '已完成'" class="action-btn download-btn" @click="handleDownload(file)"
          size="small" type="success" circle>
          <i class="fas fa-download"></i>
        </el-button>
        <el-button class="action-btn delete-btn" @click="handleDelete(file)" size="small" type="danger" circle>
          <i class="fas fa-trash"></i>
        </el-button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  fileList: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['preview', 'download', 'delete', 'clearAll', 'selection-change'])

// 状态显示映射（后端返回中文状态值，此处做翻译映射）
const statusDisplayMap = {
  '翻译中': 'manga.vip.statusTranslating',
  '已完成': 'manga.vip.statusCompleted',
  '失败': 'manga.vip.statusFailed',
}
const displayStatus = (status) => {
  const key = statusDisplayMap[status]
  return key ? t(key) : status
}

// 选中的文件状态管理
const selectedFiles = ref({})

// 计算选中的文件数量
const selectedCount = computed(() => {
  return Object.values(selectedFiles.value).filter(Boolean).length
})

// 全选状态（只考虑已完成的文件）
const selectAll = computed({
  get() {
    const completedFiles = props.fileList.filter(file => file.status === '已完成')
    const maxSelectable = Math.min(20, completedFiles.length)
    const count = selectedCount.value
    return count > 0 && count >= maxSelectable
  },
  set(value) {
    handleSelectAll(value)
  }
})

// 部分选中状态（只考虑已完成的文件）
const isIndeterminate = computed(() => {
  const completedFiles = props.fileList.filter(file => file.status === '已完成')
  const maxSelectable = Math.min(20, completedFiles.length)
  const count = selectedCount.value
  return count > 0 && count < maxSelectable
})

// 获取选中的文件imgId列表（后端接口需要的格式）
const selectedIds = computed(() => {
  return Object.entries(selectedFiles.value)
    .filter(([id, selected]) => selected)
    .map(([id]) => {
      const file = props.fileList.find(f => f.id === id)
      return file ? file.imgId : null
    })
    .filter(imgId => imgId !== null)
})

// 监听文件列表变化，重置选择状态
watch(() => props.fileList, () => {
  const newSelectedFiles = {}
  props.fileList.forEach(file => {
    if (selectedFiles.value[file.id] !== undefined) {
      newSelectedFiles[file.id] = selectedFiles.value[file.id]
    } else {
      newSelectedFiles[file.id] = false
    }
  })
  selectedFiles.value = newSelectedFiles
}, { deep: true, immediate: true })

// 监听选中变化，通知父组件
watch(selectedIds, (newSelectedIds) => {
  emit('selection-change', newSelectedIds)
}, { deep: true })

// 格式化时间
const formatTime = (time) => {
  if (!time) return t('manga.fileList.timeJustNow')
  const now = new Date()
  const uploadTime = new Date(time)
  const diff = now - uploadTime

  if (diff < 60000) return t('manga.fileList.timeJustNow')
  if (diff < 3600000) return t('manga.fileList.timeMinutesAgo', { count: Math.floor(diff / 60000) })
  if (diff < 86400000) return t('manga.fileList.timeHoursAgo', { count: Math.floor(diff / 3600000) })
  return t('manga.fileList.timeDaysAgo', { count: Math.floor(diff / 86400000) })
}

// 处理函数
const handlePreview = (file) => {
  emit('preview', file)
}

const handleDownload = (file) => {
  emit('download', file)
}

const handleDelete = (file) => {
  emit('delete', file)
}

const handleClearAll = () => {
  selectedFiles.value = {}
  emit('clearAll')
}

// 全选/取消全选（只选择已完成的文件，最多20个）
const handleSelectAll = (value) => {
  const completedFiles = props.fileList.filter(file => file.status === '已完成')
  const filesToSelect = completedFiles.slice(0, 20) // 最多选择20个

  if (value) {
    // 全选：选择前20个已完成的文件
    filesToSelect.forEach(file => {
      selectedFiles.value[file.id] = true
    })
    // 确保其他未完成的文件不被选中
    props.fileList.forEach(file => {
      if (file.status !== '已完成' || !filesToSelect.some(f => f.id === file.id)) {
        selectedFiles.value[file.id] = false
      }
    })
  } else {
    // 取消全选：清空所有选择
    props.fileList.forEach(file => {
      selectedFiles.value[file.id] = false
    })
  }
}

// 单个文件选择
const handleFileSelect = (fileId) => {
  // 触发computed属性更新，自动通知父组件
}

// 获取状态对应的CSS类
const getStatusClass = (status) => {
  switch (status) {
    case '翻译中':
      return 'status-translating'
    case '已完成':
      return 'status-completed'
    default:
      return 'status-other'
  }
}
</script>

<style scoped>
.task-list {
  margin-top: 1rem;
}

.empty-list {
  text-align: center;
  padding: 3rem 1rem;
  color: #64748b;
}

.empty-list i {
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: #475569;
}

.select-all-bar {
  background: rgba(51, 65, 85, 0.3);
  border-radius: 8px;
  padding: 0.8rem 1.2rem;
  margin-bottom: 1rem;
  border-left: 3px solid #5eead4;
  display: flex;
  align-items: center;
}

.task-item {
  background: rgba(51, 65, 85, 0.4);
  border-radius: 10px;
  padding: 1.2rem;
  margin-bottom: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-left: 3px solid #5eead4;
  transition: all 0.3s ease;
}

.task-checkbox {
  margin-right: 1rem;
  display: flex;
  align-items: center;
}

:deep(.el-checkbox__input.is-checked .el-checkbox__inner) {
  background-color: #5eead4 !important;
  border-color: #5eead4 !important;
}

:deep(.el-checkbox__input.is-checked + .el-checkbox__label) {
  color: #5eead4 !important;
}

:deep(.el-checkbox__inner:hover) {
  border-color: #5eead4 !important;
}

:deep(.el-checkbox__input.is-disabled .el-checkbox__inner) {
  background-color: rgba(94, 234, 212, 0.1) !important;
  border-color: rgba(94, 234, 212, 0.3) !important;
  cursor: not-allowed !important;
}

:deep(.el-checkbox__input.is-disabled + .el-checkbox__label) {
  color: #64748b !important;
  cursor: not-allowed !important;
}

.task-item:hover {
  background: rgba(51, 65, 85, 0.6);
}

.task-info {
  flex: 1;
}

.task-name {
  font-weight: 600;
  margin-bottom: 0.3rem;
  color: #e2e8f0;
}

.task-time {
  font-size: 0.85rem;
  color: #94a3b8;
}

/* 状态颜色 */
.status-translating {
  color: #eab308 !important; /* 黄色 */
}

.status-completed {
  color: #22c55e !important; /* 绿色 */
}

.status-other {
  color: #ef4444 !important; /* 红色 */
}

.task-actions {
  display: flex;
  gap: 10px;
}

:deep(.action-btn.el-button--primary) {
  background: rgba(94, 234, 212, 0.1) !important;
  color: #5eead4 !important;
  border-color: rgba(94, 234, 212, 0.2) !important;
}

:deep(.action-btn.el-button--primary:hover) {
  background: rgba(94, 234, 212, 0.2) !important;
  transform: translateY(-2px);
}

:deep(.action-btn.el-button--success) {
  background: rgba(94, 234, 212, 0.1) !important;
  color: #5eead4 !important;
  border-color: rgba(94, 234, 212, 0.2) !important;
}

:deep(.action-btn.el-button--success:hover) {
  background: rgba(94, 234, 212, 0.2) !important;
  transform: translateY(-2px);
}

:deep(.action-btn.el-button--danger) {
  background: rgba(239, 68, 68, 0.1) !important;
  color: #ef4444 !important;
  border-color: rgba(239, 68, 68, 0.2) !important;
}

:deep(.action-btn.el-button--danger:hover) {
  background: rgba(239, 68, 68, 0.2) !important;
  transform: translateY(-2px);
}

@media (max-width: 500px) {
  .task-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .task-actions {
    width: 100%;
    justify-content: flex-end;
  }
}
</style>